import {useCallback, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {useDialogSlot} from 'UI/hooks/useDialogManager';

const STORAGE_KEY = 'pwa-install-dismissed';
const DIALOG_PRIORITY = 2;

/**
 * The non-standard `beforeinstallprompt` event fired by Chromium-based browsers when the
 * current page is eligible for installation as a PWA.
 */
interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    readonly userChoice: Promise<{outcome: 'accepted' | 'dismissed'; platform: string}>;
}

/**
 * Checks whether the user has already dismissed or acted on the install banner before.
 *
 * @returns `true` when a stored choice exists.
 */
const hasStoredDismissal = (): boolean => {
    if (typeof window === 'undefined') return true;

    return Boolean(localStorage.getItem(STORAGE_KEY));
};

/**
 * Checks whether the app is already running as an installed, standalone PWA.
 *
 * @returns `true` when running in standalone display mode.
 */
const isRunningStandalone = (): boolean => {
    if (typeof window === 'undefined') return false;

    // browser globals, not the experimental Node.js builtins this rule is meant to guard against.
    const iosStandalone = (window.navigator as Navigator & {standalone?: boolean}).standalone;

    return window.matchMedia('(display-mode: standalone)').matches || Boolean(iosStandalone);
};

/**
 * Custom hook for the `PwaInstallBanner` component.
 * Captures the browser's `beforeinstallprompt` event so the install prompt can be triggered
 * from a custom banner instead of relying on the browser's own UI, and tracks the user's
 * choice so the banner doesn't reappear after being dismissed or after installation.
 * Coordinates with the shared `DialogManagerProvider` (see `useDialogManager`) so it doesn't
 * overlap with other global dialogs/banners, such as the language selection popup.
 *
 * @returns An object containing:
 * - `isOpen`: Whether the banner is currently visible.
 * - `handleInstall`: Triggers the native install prompt.
 * - `handleDismiss`: Dismisses the banner and remembers the choice.
 * - `t`: The translation function for the banner's copy.
 */
export const usePwaInstallBanner = () => {
    const {t} = useTranslation();
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [dismissed, setDismissed] = useState(hasStoredDismissal);

    useEffect(() => {
        if (isRunningStandalone()) return undefined;

        /**
         * Prevents the browser's default install prompt and stores the event so it can be
         * triggered later from the custom banner.
         *
         * @param event The `beforeinstallprompt` event.
         */
        const onBeforeInstallPrompt = (event: Event) => {
            event.preventDefault();
            setDeferredPrompt(event as BeforeInstallPromptEvent);
        };

        /**
         * Marks the app as installed once the browser confirms it, hiding the banner for good.
         */
        const onAppInstalled = () => {
            localStorage.setItem(STORAGE_KEY, 'true');
            setDismissed(true);
            setDeferredPrompt(null);
        };

        window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
        window.addEventListener('appinstalled', onAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
            window.removeEventListener('appinstalled', onAppInstalled);
        };
    }, []);

    const wantsOpen = Boolean(deferredPrompt) && !dismissed;
    const isActive = useDialogSlot('pwaInstallBanner', DIALOG_PRIORITY, wantsOpen);
    const isOpen = wantsOpen && isActive;

    /**
     * Triggers the native install prompt and remembers the user's choice either way.
     */
    const handleInstall = useCallback(async () => {
        if (!deferredPrompt) return;

        await deferredPrompt.prompt();
        await deferredPrompt.userChoice;

        localStorage.setItem(STORAGE_KEY, 'true');
        setDismissed(true);
        setDeferredPrompt(null);
    }, [deferredPrompt]);

    /**
     * Dismisses the banner without installing and remembers the choice.
     */
    const handleDismiss = useCallback(() => {
        localStorage.setItem(STORAGE_KEY, 'true');
        setDismissed(true);
    }, []);

    return {
        handleDismiss,
        handleInstall,
        isOpen,
        t
    };
};